import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bookmark, Play, Pencil, Trash2 } from "lucide-react";

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString() : "Never");
const filterCount = (f) => Object.entries(f || {}).filter(([k, v]) => v !== "" && v !== false && v != null && k !== "date_field").length;

export default function SavedSearches({ currentFilters, currentQ, onRun }) {
  const [items, setItems] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState("");
  const [replaceFilters, setReplaceFilters] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/prospect-searches").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const buildFilters = () => {
    const f = { ...currentFilters };
    if (currentQ?.trim()) f.q = currentQ.trim();
    return f;
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give your saved search a name");
    setBusy(true);
    try {
      if (editItem) {
        const body = { name };
        if (replaceFilters) body.filters = buildFilters();
        await api.patch(`/prospect-searches/${editItem.id}`, body);
        toast.success("Saved search updated");
      } else {
        await api.post("/prospect-searches", { name, filters: buildFilters() });
        toast.success("Search saved");
      }
      setSaveOpen(false); setEditItem(null); setName(""); setReplaceFilters(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save search"); }
    finally { setBusy(false); }
  };

  const run = async (s) => {
    try {
      const { data } = await api.post(`/prospect-searches/${s.id}/run`);
      onRun(data.filters);
      toast.success(`Running "${data.name}"`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run search"); }
  };

  const del = async (s) => {
    try { await api.delete(`/prospect-searches/${s.id}`); toast.success("Saved search deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="saved-prospect-searches">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">My Saved Searches</span>
        <Button size="sm" variant="outline" onClick={() => { setEditItem(null); setName(""); setSaveOpen(true); }} data-testid="save-prospect-search-btn">
          <Bookmark className="w-3.5 h-3.5 mr-1.5" />Save Search</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="saved-searches-empty">No saved searches yet — set filters, then click Save Search.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 bg-secondary rounded-md px-3 py-2 text-sm" data-testid={`saved-ps-${s.id}`}>
              <div>
                <span className="font-medium">{s.name}</span>
                <span className="text-xs text-muted-foreground ml-2">{filterCount(s.filters)} filter{filterCount(s.filters) === 1 ? "" : "s"} · Created {fmtD(s.created_at)} · Last run: {fmtD(s.last_run_at)}</span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => run(s)} data-testid={`run-ps-${s.id}`} title="Run"><Play className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(s); setName(s.name); setReplaceFilters(false); setSaveOpen(true); }} data-testid={`edit-ps-${s.id}`} title="Edit"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(s)} data-testid={`delete-ps-${s.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={saveOpen} onOpenChange={(o) => { setSaveOpen(o); if (!o) setEditItem(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editItem ? "Edit saved search" : "Save this search"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Search name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Clark County High Equity Pre-Foreclosures" data-testid="ps-name-input" />
            </div>
            {editItem ? (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox checked={replaceFilters} onCheckedChange={(v) => setReplaceFilters(!!v)} data-testid="ps-replace-filters" />
                <span className="text-sm">Replace stored filters with the current filter panel settings</span>
              </label>
            ) : (
              <p className="text-xs text-muted-foreground">Saves the current filter panel settings ({filterCount(buildFilters())} active filter{filterCount(buildFilters()) === 1 ? "" : "s"}).</p>
            )}
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="ps-save-submit">{editItem ? "Update" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
